import { InfoMessage } from "components/shared/InfoMessage";
import { LoadingMessage } from "components/shared/LoadingMessage";
import { SpinnerButton } from "components/shared/SpinnerButton";
import graphql from "babel-plugin-relay/macro";
import { Message } from "shared/components/Message";
import { ChangePasswordError } from "components/user/ChangePasswordError";
import { changePassword } from "domain/user/changePassword";
import { ChangeLicensingPasswordError, changePasswordMutationResponse } from "generatedQueries/changePasswordMutation.graphql";
import { ChangePasswordQuery } from "generatedQueries/ChangePasswordQuery.graphql";
import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRelayEnvironment } from "react-relay";
import { Col, Form, Row } from "reactstrap";
import { PasswordEntryFields } from "routes/user/PasswordEntryFields";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { Query } from "shared/components/Query";
import { mapMessages } from "shared/mapMessages";
import { required } from "shared/validations/validations";
import { useIntl } from "react-intl";

export const messages = mapMessages("routes.account.changepassword", {
    changePasswordButton: "Change Password",
    oldPasswordLabel: "Old Password",
    passwordChangeSuccessMessage: "Password changed successfully",
    title: "Change Password",
});

interface IForm {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export const ChangePassword: FC = () => {
    const { formatMessage } = useIntl();
    const environment = useRelayEnvironment();
    const methods = useForm<IForm>({
        defaultValues: {
            oldPassword: "",
            confirmPassword: "",
            newPassword: "",
        },
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [changePasswordError, setChangePasswordError] = useState<boolean | ChangeLicensingPasswordError>(false);

    useEffect(() => {
        if (changePasswordError) {
            methods.setFocus("oldPassword", { shouldSelect: true })
        }
    },[changePasswordError]);

    return (
        <main>
            <Row className="justify-content-center">
                <Col xl={5} lg={6} md={7} sm={9}>
                    <h1 id="changePassword-title" className="h2"><Message message={messages.title} /></h1>
                    {
                        isComplete
                            ? <InfoMessage message={messages.passwordChangeSuccessMessage} />
                            : <Query<ChangePasswordQuery>
                                query={query}
                                variables={{}}>
                                {({ error, props }) => {
                                    if (!props && !error) {
                                        return <LoadingMessage />;
                                    }

                                    const {
                                        passwordValidationMessage,
                                        passwordValidationRegex,
                                     } = props?.configuration ?? {};

                                    return (
                                        <FormProvider
                                            {...methods}
                                        >
                                            <Form
                                                aria-labelledby="changePassword-title"
                                                onSubmit={methods.handleSubmit(submitChangePassword)}
                                                role="form"
                                            >
                                                <FormLabel
                                                    message={messages.oldPasswordLabel}
                                                    for="oldPassword"
                                                    required={true}
                                                >
                                                    <TextBox
                                                        id="oldPassword"
                                                        name="oldPassword"
                                                        register={methods.register}
                                                        control={methods.control}
                                                        type="password"
                                                        options={required<IForm, "oldPassword">()}
                                                        placeHolder={formatMessage(messages.oldPasswordLabel)}
                                                    />
                                                </FormLabel>
                                                <PasswordEntryFields
                                                    passwordField="newPassword"
                                                    passwordValidationRegex={passwordValidationRegex ?? undefined}
                                                    passwordStrengthError={passwordValidationMessage ?? undefined}
                                                />
                                                <ChangePasswordError
                                                    error={changePasswordError}
                                                    passwordStrengthError={passwordValidationMessage ?? undefined}
                                                />
                                                <SpinnerButton
                                                    message={messages.changePasswordButton}
                                                    isLoading={isSubmitting}
                                                    color="primary"
                                                />
                                            </Form>
                                        </FormProvider>
                                    );
                                }}
                            </Query>
                    }
                </Col>
            </Row >
        </main>
    );

    function submitChangePassword({ oldPassword, newPassword }: IForm) {
        setIsSubmitting(true);
        changePassword(
            environment,
            newPassword,
            oldPassword,
            onChangePasswordCompleted,
            onChangePasswordError);
    }

    function onChangePasswordCompleted(response: changePasswordMutationResponse) {
        setIsSubmitting(false);

        if (response.changeLicensingPassword?.wasSuccessful === true) {
            setIsComplete(true);
        } else {
            setChangePasswordError(response.changeLicensingPassword?.errors?.[0]?.errorCode ?? true);
        }
    }

    function onChangePasswordError() {
        setIsSubmitting(false);
        setChangePasswordError(true);
    }
};

const query = graphql`
    query ChangePasswordQuery {
        configuration {
            passwordValidationRegex
            passwordValidationMessage
        }
    }`;
